import React from 'react';
import { HintText } from '@components/input/styled.tsx';
import type { AppDispatchFn } from '@hooks/reduxHooks.ts';
import { getFieldError, isFieldErroneous } from '@routes/auth/___shared___/helpers.ts';
import {
    CSV_UPLOAD_POLLING_INTERVAL_MS,
    CSV_UPLOAD_STATUS,
    MAX_NOT_STARTED_POLLS,
} from '@routes/sidebar/admin-center/tenant-client/___pages___/configuration/___shared___/constants.ts';
import type { FormField } from '@types';
import { showToast, TOAST_TYPE } from '@utils/toast.tsx';
import type { FormInstance } from 'antd/es/form';
import dayjs from 'dayjs';

type ApiErrorsShape = Record<string, string[]>;
type BackendErrorField = { name: string; error: string };
interface FieldErrorInput {
    name: string;
    message: string;
}

export const mapApiErrorsToFieldErrors = (
    apiErrors: ApiErrorsShape | { errorFields?: BackendErrorField[] } | undefined | null,
    allowedKeys: string[],
): FieldErrorInput[] => {
    if (!apiErrors) return [];
    const canonicalByLower = Object.fromEntries(allowedKeys.map((key) => [key.toLowerCase(), key]));

    const addFieldError = (acc: FieldErrorInput[], rawName: string, message: string) => {
        if (!message) return;
        const canonical = canonicalByLower[rawName.toLowerCase()];
        if (!canonical) return;
        acc.push({ name: canonical, message });
    };

    const result: FieldErrorInput[] = [];

    const errorFields = (apiErrors as any)?.errorFields as BackendErrorField[] | undefined;
    if (Array.isArray(errorFields)) {
        errorFields.forEach(({ name, error }) => addFieldError(result, name, error));
        return result;
    }

    Object.entries(apiErrors as ApiErrorsShape).forEach(([key, messages]) => {
        const message = Array.isArray(messages) && messages.length ? messages[0] : '';
        addFieldError(result, key, message);
    });

    return result;
};

export const clearAllFieldErrors = (
    form: FormInstance,
    currentFields: FormField[],
    setFormFields: (updated: FormField[]) => void,
    fieldNames?: string[],
) => {
    const fieldNamesFromAntd = form
        .getFieldsError()
        .map((fieldState) => String(fieldState.name?.[0] ?? ''))
        .filter(Boolean);

    const fieldNamesFromLocal = currentFields
        .filter((field) => Array.isArray(field.errors) && field.errors.length > 0)
        .map((field) => String(field.name?.[0] ?? ''))
        .filter(Boolean);

    const targetNames =
        fieldNames && fieldNames.length
            ? fieldNames
            : Array.from(new Set([...fieldNamesFromAntd, ...fieldNamesFromLocal]));

    const targetNamesLower = new Set(targetNames.map((name) => name.toLowerCase()));

    const updatedFieldsCleared = currentFields.map((field) => {
        const fieldName = String(field?.name?.[0] ?? '');
        if (fieldName && targetNamesLower.has(fieldName.toLowerCase())) {
            return { ...field, errors: [] };
        }
        return field;
    });

    setFormFields(updatedFieldsCleared);
};

export const renderErrorIfProvided = (fieldName: string, formFields: FormField[]) =>
    isFieldErroneous(fieldName, formFields) && (
        <HintText $showByDefault className="has-error">
            {getFieldError(fieldName, formFields)}
        </HintText>
    );

export const formatDateByPreferredFormat = (value: unknown, format: string): string | null => {
    if (!value) return null;

    const d = dayjs(value as any);

    if (!d.isValid()) return null;

    return d.format(format);
};

export const showActivationToastIfNecessary = <T,>(params: {
    notify: any;
    selectedRowInitialData: T | null;
    isActive: boolean;
    getInitialIsActive: (row: T) => boolean;
    message: React.ReactNode;
}) => {
    const { notify, selectedRowInitialData, isActive, getInitialIsActive, message } = params;

    if (!selectedRowInitialData) return;

    const initialIsActive = getInitialIsActive(selectedRowInitialData);
    if (isActive === initialIsActive) return;

    showToast(notify, null, message, TOAST_TYPE.INFO);
};

type CsvUploadRequest = {
    clientId: number;
    tenantAlias: string;
    id: string;
};

type CsvUploadStatusResponse = {
    uploadStatus?: string | null;
};

type CsvUploadParams = {
    uploadedCsvId: string;
    clientId: number;
    tenantAlias: string;
    dispatch: AppDispatchFn;
    getCsvUpload: (payload: CsvUploadRequest) => any;
    setCsvUploadState: (state: null) => any;
    setIsCsvImportFailed: (value: boolean) => any;
    intervalMs?: number;
    maxNotStartedPolls?: number;
};

const isTerminalUploadStatus = (uploadStatus?: string | null) =>
    uploadStatus === CSV_UPLOAD_STATUS.SUCCESS ||
    uploadStatus === CSV_UPLOAD_STATUS.ERROR ||
    uploadStatus === CSV_UPLOAD_STATUS.PARTIAL_SUCCESS ||
    uploadStatus === CSV_UPLOAD_STATUS.ERROR_BAD_CSV_FORMAT;

/**
 * Starts CSV upload status polling with a bounded retry guard.
 *
 * Some CSV imports can get stuck in NotStarted backend state.
 * Without a limit, the frontend keeps polling forever and the user sees an endless loading state.
 *
 * This helper is shared by Crew Users, Shore Users, and Vessels imports to keep the behavior
 * consistent: terminal statuses stop polling immediately, while stuck NotStarted status
 * is treated as a failed import after the configured number of polls.
 */
export const startCsvUploadPolling = ({
    uploadedCsvId,
    clientId,
    tenantAlias,
    dispatch,
    getCsvUpload,
    setCsvUploadState,
    setIsCsvImportFailed,
    intervalMs = CSV_UPLOAD_POLLING_INTERVAL_MS,
    maxNotStartedPolls = MAX_NOT_STARTED_POLLS,
}: CsvUploadParams) => {
    let isStopped = false;
    let intervalId: number | null = null;
    let isPollingRequestInFlight = false;
    let notStartedPollCount = 0;

    const clearPolling = () => {
        if (intervalId !== null) {
            window.clearInterval(intervalId);
            intervalId = null;
        }
    };

    const failImport = () => {
        isStopped = true;
        clearPolling();
        dispatch(setIsCsvImportFailed(true));
        dispatch(setCsvUploadState(null));
    };

    const pollUploadStatus = () => {
        if (isStopped || isPollingRequestInFlight) return;

        isPollingRequestInFlight = true;

        (dispatch(getCsvUpload({ clientId, tenantAlias, id: uploadedCsvId })) as Promise<CsvUploadStatusResponse>)
            .then((response) => {
                if (isStopped) return;

                const uploadStatus = response?.uploadStatus;

                if (isTerminalUploadStatus(uploadStatus)) {
                    notStartedPollCount = 0;
                    clearPolling();
                    return;
                }

                if (uploadStatus === CSV_UPLOAD_STATUS.NOT_STARTED) {
                    notStartedPollCount += 1;

                    if (notStartedPollCount >= maxNotStartedPolls) {
                        failImport();
                    }

                    return;
                }

                notStartedPollCount = 0;
            })
            .catch(() => {
                if (!isStopped) {
                    failImport();
                }
            })
            .finally(() => {
                isPollingRequestInFlight = false;
            });
    };

    pollUploadStatus();

    intervalId = window.setInterval(() => {
        pollUploadStatus();
    }, intervalMs);

    return () => {
        isStopped = true;
        clearPolling();
        notStartedPollCount = 0;
        isPollingRequestInFlight = false;
    };
};
